import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import AdminSidebar, { permForPath } from './AdminSidebar';
import NoAccess from './NoAccess';
import SiteSwitcher from './SiteSwitcher';
import CommandPalette from './CommandPalette';
import HelpDrawer from './HelpDrawer';
import { DialogProvider } from './Dialog';
import useAdminMe from '../../hooks/useAdminMe';
import './AdminLayout.css';

// Tier 3 Item 36 — swap to the admin-scoped manifest while inside /admin/*.
// Service worker scope already differs because customer SW is under '/' and
// the admin manifest declares scope '/admin' — Chrome treats them as
// independent PWAs for install purposes.
function useAdminManifest() {
  useEffect(() => {
    const existing = document.querySelector('link[rel="manifest"]');
    const prevHref = existing?.getAttribute('href');
    let injected = null;
    if (existing) {
      existing.setAttribute('href', '/manifest-admin.webmanifest');
    } else {
      injected = document.createElement('link');
      injected.rel = 'manifest';
      injected.href = '/manifest-admin.webmanifest';
      document.head.appendChild(injected);
    }
    // Theme color matches admin chrome
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute('content');
    if (themeMeta) themeMeta.setAttribute('content', '#0f172a');
    return () => {
      if (injected) injected.remove();
      else if (existing && prevHref) existing.setAttribute('href', prevHref);
      if (themeMeta && prevTheme) themeMeta.setAttribute('content', prevTheme);
    };
  }, []);
}

// Bottom mobile nav for the PWA install. Each tab carries the `perm` its
// destination needs, so the bar reflects the admin's role (RBAC).
const MOBILE_TABS = [
  { to: '/admin',           label: 'Home',     icon: '◧' },
  { to: '/admin/orders',    label: 'Orders',   icon: '🧾', perm: 'orders' },
  { to: '/admin/products',  label: 'Products', icon: '📦', perm: 'products' },
  { to: '/admin/customers', label: 'Customers',icon: '👥', perm: 'users' },
  { to: '/admin/inventory', label: 'Stock',    icon: '🏷️', perm: 'inventory' },
];

/**
 * Wraps every /admin/* page. Provides sidebar nav + top bar + main slot.
 *
 * `breadcrumbs` accepts EITHER a pre-rendered React node OR an array of
 * `{ label, to? }` segments (the more common usage). Trailing segments
 * without a `to` render as plain text — that's the "current page".
 */
function renderBreadcrumbs(crumbs) {
  if (!crumbs) return null;
  if (!Array.isArray(crumbs)) return crumbs;
  return crumbs.map((c, i) => {
    const sep = i > 0 ? <span aria-hidden="true" style={{ margin: '0 6px', opacity: 0.4 }}>›</span> : null;
    if (c && typeof c === 'object' && c.label !== undefined) {
      return (
        <React.Fragment key={i}>
          {sep}
          {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{sep}{c}</React.Fragment>;
  });
}

export default function AdminLayout({ title, actions, breadcrumbs, children }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const { can, role, loaded, isSuper, viewAs, setViewAs } = useAdminMe();
  useAdminManifest();

  // Role-filtered bottom tabs, capped at 4 so the bar never overflows on phones.
  const mobileTabs = MOBILE_TABS.filter(t => can(t.perm)).slice(0, 4);

  // Central client-side access guard: if the current path needs a perm this
  // admin lacks (e.g. they URL-hacked a hidden section), show NoAccess instead
  // of the page. Only once /me has loaded, to avoid a flash before perms arrive.
  // The server still enforces independently — this is just friendly UX.
  const neededPerm = permForPath(location.pathname);
  const blocked = loaded && !can(neededPerm);

  return (
    <DialogProvider>
    <div className="admin-layout">
      <CommandPalette />
      <HelpDrawer />
      <AdminSidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <div className={`admin-layout__main${navOpen ? ' admin-layout__main--shifted' : ''}`}>
        <header className="admin-layout__topbar">
          <button
            type="button"
            className="admin-layout__menu-toggle"
            onClick={() => setNavOpen(o => !o)}
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
          >☰</button>
          <div className="admin-layout__title-block">
            {breadcrumbs && <nav className="admin-layout__breadcrumbs" aria-label="Breadcrumb">{renderBreadcrumbs(breadcrumbs)}</nav>}
            {title && <h1 className="admin-layout__title">{title}</h1>}
          </div>
          <div className="admin-layout__actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {role && role !== 'super_admin' && (
              <span
                title={`Signed in as ${role}`}
                style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                         background: 'var(--bg-hover, #eef2ff)', color: 'var(--primary-color, #3b4cca)',
                         padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}
              >{role}</span>
            )}
            {isSuper && (
              <select
                value={viewAs} onChange={e => setViewAs(e.target.value)}
                title="Preview the admin panel as another role"
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6 }}
              >
                <option value="">View as: Super admin</option>
                <option value="admin">View as: Admin</option>
                <option value="moderator">View as: Moderator</option>
                <option value="support">View as: Support</option>
              </select>
            )}
            <SiteSwitcher />
            {actions}
          </div>
        </header>
        <main className="admin-layout__content" role="main">
          {blocked ? <NoAccess perm={neededPerm} /> : (children || <Outlet />)}
        </main>
        <nav className="admin-mobile-tabs" aria-label="Quick navigation">
          {mobileTabs.map(t => {
            const active = t.to === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(t.to);
            return (
              <Link key={t.to} to={t.to} className={`admin-mobile-tabs__tab${active ? ' is-active' : ''}`}>
                <span className="admin-mobile-tabs__icon">{t.icon}</span>
                <span className="admin-mobile-tabs__label">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
    </DialogProvider>
  );
}
